import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n, formatCurrency, formatDate } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { PgcAccountPicker } from '@/components/PgcAccountPicker';

export default function Income() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const [i, a] = await Promise.all([api.get('/incomes'), api.get('/pgc-accounts', { params: { grupo: 7 } })]);
    setItems(i.data); setAccounts(a.data);
  }, []);
  useEffect(() => { load(); }, [load, load]);

  return (
    <div className="space-y-4" data-testid="income-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">{t('nav_income')}</h1>
        <Button onClick={() => setOpen(true)} data-testid="income-new-button"><Plus className="h-4 w-4 mr-1" /> {t('new_income')}</Button>
      </div>
      {items.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <TrendingUp className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Sin ingresos. Las ventas TPV se registran automáticamente al cerrar tickets.' : 'Sem receitas. As vendas POS são registradas automaticamente ao fechar tickets.'}</p>
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0"><div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]"><tr>
              <th className="text-left px-3 py-2 text-xs uppercase">{t('date')}</th>
              <th className="text-left px-3 py-2 text-xs uppercase">{t('description')}</th>
              <th className="text-left px-3 py-2 text-xs uppercase">{t('pgc_account')}</th>
              <th className="text-right px-3 py-2 text-xs uppercase">{t('base')}</th>
              <th className="text-right px-3 py-2 text-xs uppercase">{t('iva')}</th>
              <th className="text-right px-3 py-2 text-xs uppercase">{t('total')}</th>
            </tr></thead>
            <tbody>{items.map(it => (
              <tr key={it.id} className="border-t hover:bg-[hsl(var(--muted))]/40" data-testid={`income-row-${it.id}`}>
                <td className="px-3 py-2 text-xs">{formatDate(it.fecha, lang)}</td>
                <td className="px-3 py-2 max-w-xs truncate">{it.descripcion}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="font-mono text-xs">{it.pgc_codigo}</Badge></td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">{formatCurrency(it.base, lang)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-mono text-xs">{it.iva_pct}%</td>
                <td className="px-3 py-2 text-right tabular-nums font-mono font-semibold">{formatCurrency(it.total, lang)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div></CardContent></Card>
      )}
      <IncomeDialog open={open} onOpenChange={setOpen} accounts={accounts} t={t} lang={lang} onSaved={load} />
    </div>
  );
}

function IncomeDialog({ open, onOpenChange, accounts, t, lang, onSaved }) {
  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    descripcion: '', pgc_codigo: '700', base: 0, iva_pct: 10, metodo_pago: 'tarjeta', notas: '',
  });
  useEffect(() => {
    if (open) setForm({
      fecha: new Date().toISOString().slice(0, 10),
      descripcion: '', pgc_codigo: '700', base: 0, iva_pct: 10, metodo_pago: 'tarjeta', notas: '',
    });
  }, [open]);
  const save = async () => {
    const payload = { ...form, fecha: new Date(form.fecha).toISOString() };
    await api.post('/incomes', payload);
    toast.success('OK'); onOpenChange(false); onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="income-dialog">
        <DialogHeader><DialogTitle>{t('new_income')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('date')}</Label><Input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} /></div>
            <div><Label>{t('payment_method')}</Label>
              <Select value={form.metodo_pago} onValueChange={v => setForm({ ...form, metodo_pago: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">{t('cash')}</SelectItem><SelectItem value="tarjeta">{t('card')}</SelectItem><SelectItem value="transferencia">{t('transfer')}</SelectItem><SelectItem value="otro">{t('other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>{t('description')}</Label><Input value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} data-testid="income-description-input" /></div>
          <div><Label>{t('pgc_account')}</Label>
            <PgcAccountPicker accounts={accounts} value={form.pgc_codigo} onChange={v => setForm({ ...form, pgc_codigo: v })} testId="income-pgc-picker" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('base')}</Label><Input type="number" step="0.01" value={form.base} onChange={e => setForm({ ...form, base: Number(e.target.value) })} /></div>
            <div><Label>{t('iva_pct')}</Label>
              <Select value={String(form.iva_pct)} onValueChange={v => setForm({ ...form, iva_pct: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="0">0%</SelectItem><SelectItem value="4">4%</SelectItem><SelectItem value="10">10%</SelectItem><SelectItem value="21">21%</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.descripcion} data-testid="income-save-button">{t('save')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
